import React, { useState } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { Modal } from 'components/common/modal'
import { observer } from 'mobx-react'
import { PositionApplicationStore } from 'stores/position-application'
import { theme } from 'infra/theme'
import { BtnSize, BtnType, Button } from 'components/common/button'
import { Column, Row } from 'components/common/layout'
import {
  BaseText,
  BoldText,
  MediumText,
  SemiBoldText,
} from 'components/common/text'
import { getAddedDays, getProfileCompleteStatus } from 'utils/formatters'
import { CheckCircleIcon } from 'components/common/icon'
import { useIsDesktopCSR } from 'utils/hooks'
import { NextRouter, useRouter } from 'next/router'
import {
  usePreAnalysis,
  useRequestFormOptions,
  useScoutMyRequest,
  useScoutRequestMyInfoFillStatus,
  useUser,
} from 'apis/hooks'
import {
  DESIGN_POSITION_ID,
  MARKETING_POSITION_ID,
  PLAN_MANAGER_POSITION_ID,
} from 'infra/constants'
import { PreAnalysis } from 'components/scouts/request-form/condition/pre-analysis'
import { ExpectedProposal } from 'components/scouts/request-form/condition/expected-proposal'
import { ProfileFillRequestModal } from 'components/scouts/request-form/profile/profile-fill-request-modal'
import { RoutePaths, RouterQueryKey } from 'types/router'
import { analytics } from 'infra/analytics'
import { ProfileCategoryId } from 'types/common'
import { getScoutRequestMyInfoFillStatusType } from 'components/scouts/profile/my-profile-bottom-bar'

export const PositionApplicationDoneModal: React.FC<{
  store: PositionApplicationStore
  onClose: () => void
}> = observer(({ store, onClose }) => {
  const { data: profile } = useScoutMyRequest()
  if (!store.positionDetail) return null
  const { isAbleToApply } = getProfileCompleteStatus(profile)
  const { data: initialInfoFillStatus } = useScoutRequestMyInfoFillStatus()

  const isNormalApplication =
    isAbleToApply || // "그룹바이 프로필"을 포함해서 지원했거나
    getScoutRequestMyInfoFillStatusType(initialInfoFillStatus) === 'WORKING' || // 이미 프로필 대신 작성을 요청했거나
    !store.hasAttachment // 첨부자료가 없으면 => 기본 모달
  return (
    <Modal width={600} onClose={onClose} disableCloseBtn disableBackgroundClick>
      {isNormalApplication ? (
        <PositionApplicationDoneWithProfile store={store} onClose={onClose} />
      ) : (
        <PositionApplicationDoneWithoutProfile
          store={store}
          onClose={onClose}
        />
      )}
    </Modal>
  )
})

const redirectAfterApplication = (router: NextRouter) => {
  // 1. 내커딱공에서 왔을 때 -> 내커딱공으로 돌려보냄
  const curationId = Number(router.query.curationId)
  const isCuration = !!curationId
  if (isCuration) {
    if (router.pathname !== RoutePaths.MY_CURATION)
      router.replace(`/my-curation?curationId=${curationId}`)
    return
  }
  const isMatchedPage =
    router.pathname === RoutePaths.POSITIONS_MATCHED || // 데스크탑일 때
    router.query[RouterQueryKey.FROM_SITE] === // 모바일일 때
      RoutePaths.POSITIONS_MATCHED
  // 2. 조건딱공고에서 왔을 때 -> 조건딱공고로 돌려보냄
  if (isMatchedPage) {
    if (router.pathname !== RoutePaths.POSITIONS_MATCHED)
      router.replace(RoutePaths.POSITIONS_MATCHED)
    return
  }
  // 3. 그외, 기본값은 채용공고 목록으로 돌려보냄
  if (router.pathname !== RoutePaths.POSITIONS)
    router.replace(RoutePaths.POSITIONS)
}

export const PositionApplicationDoneWithProfile: React.FC<{
  store: PositionApplicationStore
  onClose: () => void
}> = observer(({ store, onClose }) => {
  const { data: user } = useUser()
  const { data: myProfile } = useScoutMyRequest()
  const router = useRouter()
  const isDesktop = useIsDesktopCSR()
  const isVisibleTipNotice =
    !!user?.interestedPositionTypes.find(
      (item) =>
        item.parentId === MARKETING_POSITION_ID ||
        item.parentId === DESIGN_POSITION_ID ||
        item.parentId === PLAN_MANAGER_POSITION_ID,
    ) &&
    !myProfile?.links.length &&
    !myProfile?.files.length
  const buttonSize = isDesktop ? undefined : BtnSize.MEDIUM
  return (
    <ContentBox>
      <Column style={{ gap: 24 }}>
        <Column style={{ gap: 8, alignItems: 'center' }}>
          <CheckCircleIcon size={isDesktop ? 32 : 24} />
          <PageTitle>지원을 완료했어요!</PageTitle>
        </Column>
        <ExplainText>
          지원 결과는{' '}
          <span
            style={{
              fontWeight: theme.fontWeight.bold,
              color: theme.color.secondary,
            }}
          >
            {dayjs(getAddedDays(14)).format('M월 D일')}
          </span>
          까지 알림톡으로 안내드려요.
          <br />
          좋은 결과 있으시길 바랍니다.
        </ExplainText>
      </Column>
      <ApplyInfoContainer>
        <ApplyInfoRow>
          <PositionText>기업명</PositionText>
          <SemiBoldText>{store.startupDetail?.name}</SemiBoldText>
        </ApplyInfoRow>
        <ApplyInfoRow>
          <PositionText>포지션 · 경력</PositionText>
          <SemiBoldText>
            {store.positionDetail?.name} · {store.positionDetail?.careerType}
          </SemiBoldText>
        </ApplyInfoRow>
      </ApplyInfoContainer>
      {isVisibleTipNotice && (
        <Column style={{ alignItems: 'center', gap: 8 }}>
          <TipMainText>
            <span>Tip.</span> 서류합격률을 높여보시겠어요?
          </TipMainText>
          <ExplainText>
            포트폴리오 없이 지원하면{' '}
            <span style={{ color: theme.color.alert }}>합격 가능성</span>이
            낮아요!
          </ExplainText>
        </Column>
      )}
      <Column style={{ gap: 8 }}>
        <Button
          size={buttonSize}
          onClick={() => {
            redirectAfterApplication(router)
            onClose()
          }}
        >
          다른 채용 공고 둘러보기
        </Button>
        <Button
          variant={BtnType.OUTLINE}
          size={buttonSize}
          onClick={() => {
            router.replace(
              isVisibleTipNotice
                ? {
                    pathname: RoutePaths.MY_PROFILE,
                    query: {
                      [RouterQueryKey.RESOLVED_INFO_FILL]:
                        ProfileCategoryId.RESUME,
                    },
                  }
                : RoutePaths.MY_ACTIVITIES,
            )
            onClose()
          }}
        >
          {isVisibleTipNotice
            ? '👉 프로필에 포트폴리오 첨부하기'
            : '지원 정보 보기'}
        </Button>
      </Column>
    </ContentBox>
  )
})

const PositionApplicationDoneWithoutProfile: React.FC<{
  store: PositionApplicationStore
  onClose: () => void
}> = observer(({ store, onClose }) => {
  const { data: user } = useUser()
  const { data: preAnalysis } = usePreAnalysis({
    positions:
      user?.interestedPositionTypes.map((position) => position.id) || [],
    experience: user?.experience || 0,
  })
  const { data: options } = useRequestFormOptions()
  const [isTossModalOpen, setIsTossModalOpen] = useState(false)
  const isDesktop = useIsDesktopCSR()
  const router = useRouter()
  const btnSize = !isDesktop ? BtnSize.MEDIUM : undefined

  if (!user || !preAnalysis || !options) return null
  return (
    <>
      <Column>
        <ContentBox>
          <Column style={{ gap: 8 }}>
            <TitleText>지원 완료!</TitleText>
            <SubText>
              지원 결과는 <b>{dayjs(getAddedDays(14)).format('M월 D일')}</b>
              까지 알림톡으로 안내드려요.
            </SubText>
          </Column>
          <PreAnalysis user={user} preAnalysis={preAnalysis} />
          <ExpectedProposal user={user} options={options} />
        </ContentBox>
        <Bottom>
          <GrayText>
            지금 첨부한 파일/링크로 1분 만에 프로필을 완성해 보세요
          </GrayText>
          <Row>
            <CtaBtn
              variant={BtnType.SUPER_LOW}
              size={btnSize}
              style={{ flex: 1 }}
              onClick={() => {
                redirectAfterApplication(router)
                onClose()
              }}
            >
              나중에 하기
            </CtaBtn>
            <CtaBtn
              size={btnSize}
              style={{ flex: 2.5 }}
              onClick={() => setIsTossModalOpen(true)}
            >
              1분 프로필 완성하기
            </CtaBtn>
          </Row>
        </Bottom>
      </Column>
      {isTossModalOpen && (
        <ProfileFillRequestModal
          files={store.files}
          links={store.links.filter((item) => item.trim() !== '')}
          onClose={() => setIsTossModalOpen(false)}
          onSuccess={() => {
            router.push(RoutePaths.REQUEST_FORM_TOSS)
            analytics.track('click_profile_fill_request_from_application')
            onClose()
          }}
          hasTossLinkModal={false}
        />
      )}
    </>
  )
})

const PositionText = styled(MediumText)`
  color: ${(p) => p.theme.color.gray2};
  text-align: end;
`

const ContentBox = styled(Column)`
  gap: 40px;
  padding: 32px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    gap: 32px;
    padding: 32px 20px;
  }
`

const PageTitle = styled(SemiBoldText)`
  font-size: ${(p) => p.theme.fontSize.h3};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.h4};
  }
`

const ExplainText = styled(BaseText)`
  line-height: 1.6;
  text-align: center;
  color: ${(p) => p.theme.color.black4};
  font-size: ${(p) => p.theme.fontSize.desc};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: 14px;
  }
`

const ApplyInfoContainer = styled(Column)`
  gap: 16px;
  padding: 20px;
  justify-content: center;
  border-top: 1px solid ${(props) => props.theme.color.gray3};
  border-bottom: 1px solid ${(props) => props.theme.color.gray3};
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 20px 0;
    gap: 12px;
  }
`

const ApplyInfoRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
`

const TipMainText = styled(BoldText)`
  font-size: ${(p) => p.theme.fontSize.h4};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.h5};
  }
  span {
    color: ${(p) => p.theme.color.primary};
  }
`

const TitleText = styled(BoldText)`
  color: ${(p) => p.theme.color.black4};
  @media ${(p) => p.theme.deviceSize.desktop} {
    font-size: ${(p) => p.theme.fontSize.h5};
  }
`

const SubText = styled(BaseText)`
  color: ${(p) => p.theme.color.gray1};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
  }
`

const Bottom = styled(Column)`
  bottom: 0;
  position: sticky;
  padding-top: 8px;
  gap: 8px;
  background-color: ${(p) => p.theme.color.white1};
`

const CtaBtn = styled(Button)`
  border-radius: 0;
  padding: 16px;
`

const GrayText = styled(MediumText)`
  text-align: center;
  color: ${(p) => p.theme.color.gray1};
  line-height: 1.6;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
  }
`
